import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import { format } from 'date-fns';
import { formatCurrency } from '../../utils/calculations';
import { getTransactionAPI } from '../../api';
import { downloadTransactionExcel } from '../../utils/fileDownload';
import { CREDIT_TRANSACTION_TYPES } from '../../constants';
import toast from 'react-hot-toast';
import { LoadingSpinner } from '../common';

// Where the "Back" button returns to - keyed by the `from` value passed via
// navigate(..., { state: { from } }) when arriving at the viewer, so it goes
// back to whichever list page (Search or Daily Report) the user came from.
// Defaults to Search since that's the original entry point for viewer pages.
const BACK_TARGETS = {
  search: { path: '/search', label: 'Back to Search' },
  'daily-report': { path: '/daily-report', label: 'Back to Daily Report' }
};

const TransactionViewer = ({ transactionId, transactionType }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const backTarget = BACK_TARGETS[location.state?.from] || BACK_TARGETS.search;
  const [transaction, setTransaction] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadTransaction = useCallback(async () => {
    setLoading(true);
    try {
      const transactionAPI = getTransactionAPI(transactionType);
      const response = await transactionAPI.getById(transactionId);
      setTransaction(response.data);

      // Load items (they should be included in the response with relationships)
      if (response.data.items) {
        setItems(response.data.items);
      }
    } catch (error) {
      console.error('Error loading transaction:', error);
      toast.error('Failed to load transaction');
    } finally {
      setLoading(false);
    }
  }, [transactionId, transactionType]);

  useEffect(() => {
    loadTransaction();
  }, [loadTransaction]);

  const handleDownload = async () => {
    try {
      const transactionAPI = getTransactionAPI(transactionType);
      const response = await transactionAPI.downloadPDF(transactionId);
      downloadTransactionExcel(response, transactionType, transactionId);
      toast.success('Download complete');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  const handleBack = () => {
    navigate(backTarget.path);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!transaction) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Transaction not found</Typography>
        <Button startIcon={<BackIcon />} onClick={handleBack} sx={{ mt: 2 }}>
          {backTarget.label}
        </Button>
      </Box>
    );
  }

  const typeNames = {
    cash_sale: 'Cash Sale',
    refund: 'Refund',
    invoice: 'Invoice',
    credit_note: 'Credit Note'
  };

  const isCashOrRefund = transactionType === 'cash_sale' || transactionType === 'refund';
  const isCreditType = CREDIT_TRANSACTION_TYPES.includes(transactionType);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={handleBack}
        >
          {backTarget.label}
        </Button>

        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
        >
          Download Excel
        </Button>
      </Box>

      {/* Transaction Details */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          // Same warning accent as the Refund/Credit Note entry forms, so a
          // record viewed from Search reads the same way as one being created.
          ...(isCreditType && {
            borderLeft: '6px solid',
            borderColor: 'warning.main',
            bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08),
          }),
        }}
      >
        <Typography variant="h5" gutterBottom>
          {typeNames[transactionType]} #{transactionId}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary">
              Date
            </Typography>
            <Typography variant="body1">
              {transaction.timestamp ? format(new Date(transaction.timestamp), 'dd/MM/yyyy HH:mm:ss') : '-'}
            </Typography>
          </Grid>

          {isCashOrRefund ? (
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">
                Customer Name
              </Typography>
              <Typography variant="body1">
                {transaction.customer?.customer || '-'}
              </Typography>
            </Grid>
          ) : (
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">
                Account
              </Typography>
              <Typography variant="body1">
                {transaction.account?.name || '-'} ({transaction.account?.sage || '-'})
              </Typography>
            </Grid>
          )}

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary">
              Order Number
            </Typography>
            <Typography variant="body1">
              {transaction.order_number || '-'}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary">
              A/C #
            </Typography>
            <Typography variant="body1">
              {transaction.account?.sage || '-'}
            </Typography>
          </Grid>
        </Grid>

        {/* Totals */}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography variant="caption" color="text.secondary">
                Subtotal
              </Typography>
              <Typography variant="h6">
                {formatCurrency(transaction.subtotal)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography variant="caption" color="text.secondary">
                VAT (23%)
              </Typography>
              <Typography variant="h6">
                {formatCurrency(transaction.vat)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography variant="caption" color="text.secondary">
                Total
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatCurrency(transaction.total)}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Line Items */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Line Items
        </Typography>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Registration</strong></TableCell>
                <TableCell><strong>Part No.</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell align="right"><strong>Qty</strong></TableCell>
                <TableCell align="right"><strong>Price</strong></TableCell>
                <TableCell align="right"><strong>Discount</strong></TableCell>
                <TableCell align="right"><strong>Subtotal</strong></TableCell>
                <TableCell align="right"><strong>VAT</strong></TableCell>
                <TableCell align="right"><strong>Total</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography color="text.secondary">No items found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.registration || '-'}</TableCell>
                    <TableCell>{item.product?.part_number || '-'}</TableCell>
                    <TableCell>{item.description || '-'}</TableCell>
                    <TableCell align="right">{item.qty}</TableCell>
                    <TableCell align="right">{formatCurrency(item.price)}</TableCell>
                    <TableCell align="right">
                      {item.discount === -1 ? 'NETT' : item.discount === 0 ? 'Retail' : `${item.discount}%`}
                    </TableCell>
                    <TableCell align="right">{formatCurrency(item.subtotal)}</TableCell>
                    <TableCell align="right">{formatCurrency(item.vat)}</TableCell>
                    <TableCell align="right"><strong>{formatCurrency(item.total)}</strong></TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default TransactionViewer;
